/**
 * Figma Node IDs:
 * Design Frame: 1:8019 (Button, function=soft, shape=circle)
 * Instance IDs: 1:8019
 * Composite: false
 * Last Updated: 2026-07-03
 * Generated by: Claude Code figma-component command
 */
import * as React from "react";
import { defineMessages, useIntl } from "react-intl";

import { srOnly } from "@labxchange/design_tokens";
import { Headphones, Pause } from "@labxchange/icons";

import { LxButton } from "../LxButton";
import type { ButtonSize } from "../LxButton/constants";
import { audioButtonWrapper } from "./AudioButton.css";

const messages = defineMessages({
  play: {
    id: "audio_button.play",
    defaultMessage: "Play audio",
    description: "Accessible label for the audio button when audio is paused",
  },
  pause: {
    id: "audio_button.pause",
    defaultMessage: "Pause audio",
    description: "Accessible label for the audio button when audio is playing",
  },
  statusPlaying: {
    id: "audio_button.status_playing",
    defaultMessage: "Playing",
    description: "Screen-reader status announced when audio starts playing",
  },
  statusPaused: {
    id: "audio_button.status_paused",
    defaultMessage: "Paused",
    description: "Screen-reader status announced when audio is paused",
  },
  statusUnavailable: {
    id: "audio_button.status_unavailable",
    defaultMessage: "Audio unavailable",
    description: "Screen-reader status announced when the audio fails to load",
  },
});

/**
 * Module-level registry of mounted audio elements. Used to enforce
 * single-active-clip playback: when one clip starts, all others pause.
 */
const activeAudioElements = new Set<HTMLAudioElement>();

type PlaybackStatus = "" | "playing" | "paused" | "error";

export interface AudioButtonProps {
  src: string;
  /** Overrides the accessible label entirely. */
  ariaLabel?: string;
  className?: string;
  testId?: string;
  size?: ButtonSize;
}

/**
 * Shared audio prop consumed identically by Foundation Text components
 * (Body, Heading, Label). When `audioSrc` is provided, the component renders
 * an AudioButton inline at the start of its text content.
 */
export interface FoundationAudioProps {
  audioSrc?: string;
}

export const AudioButton = ({
  src,
  ariaLabel,
  className,
  testId,
  size = "small",
}: AudioButtonProps) => {
  const intl = useIntl();
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [status, setStatus] = React.useState<PlaybackStatus>("");

  React.useEffect(() => {
    if (!src) {
      return undefined;
    }

    const audio = new Audio(src);
    audioRef.current = audio;
    activeAudioElements.add(audio);

    const handlePlay = () => {
      setIsPlaying(true);
      setStatus("playing");
      activeAudioElements.forEach((element) => {
        if (element !== audio) {
          element.pause();
        }
      });
    };
    const handlePause = () => {
      setIsPlaying(false);
      setStatus("paused");
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setStatus("paused");
    };
    const handleError = () => {
      setIsPlaying(false);
      setStatus("error");
    };

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.pause();
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      activeAudioElements.delete(audio);
    };
  }, [src]);

  const handleToggle = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (isPlaying) {
      audio.pause();
    } else {
      const playResult = audio.play();
      if (playResult && typeof playResult.catch === "function") {
        playResult.catch(() => setStatus("error"));
      }
    }
  };

  const label =
    ariaLabel ?? intl.formatMessage(isPlaying ? messages.pause : messages.play);

  let statusText = "";
  if (status === "playing") {
    statusText = intl.formatMessage(messages.statusPlaying);
  } else if (status === "paused") {
    statusText = intl.formatMessage(messages.statusPaused);
  } else if (status === "error") {
    statusText = intl.formatMessage(messages.statusUnavailable);
  }

  return (
    <span
      className={
        className ? `${audioButtonWrapper} ${className}` : audioButtonWrapper
      }
    >
      <LxButton
        variant="soft"
        shape="circle"
        size={size}
        Icon={isPlaying ? Pause : Headphones}
        ariaLabel={label}
        onClickHandler={handleToggle}
        disabled={!src}
        testId={testId}
      />
      <span aria-live="polite" className={srOnly}>
        {statusText}
      </span>
    </span>
  );
};

export default AudioButton;
